<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  summary: { type: Object, default: null },
});

const { t } = useI18n();

const pctDelta = (current, previous) => {
  if (!previous || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
};

const pctOfTotal = s => {
  const total = (s?.positive || 0) + (s?.negative || 0) + (s?.neutral || 0);
  if (!total) return 0;
  return Math.round(((s.positive || 0) / total) * 100);
};

const cards = computed(() => {
  const s = props.summary || {};
  const prev = s.previous || {};
  const sentiment = s.sentiment || { positive: 0, negative: 0, neutral: 0 };
  const prevSentiment = prev.sentiment || { positive: 0, negative: 0, neutral: 0 };
  const engagement = s.engagement || { total: 0 };
  const prevEngagement = prev.engagement || { total: 0 };

  const currPct = pctOfTotal(sentiment);
  const prevPct = pctOfTotal(prevSentiment);

  const trend = s.trend || 0;

  return [
    {
      label: t('SOCIAL_MONITORING.CARDS.TOTAL_MENTIONS'),
      value: s.totalMentions ?? 0,
      delta: pctDelta(s.totalMentions || 0, prev.totalMentions || 0),
      icon: 'i-lucide-message-square',
      color: 'text-blue-600',
    },
    {
      label: t('SOCIAL_MONITORING.CARDS.POSITIVE_PCT'),
      value: `${currPct}%`,
      delta: prevPct === 0 ? null : currPct - prevPct,
      deltaSuffix: 'pp',
      icon: 'i-lucide-thumbs-up',
      color: 'text-emerald-600',
    },
    {
      label: t('SOCIAL_MONITORING.CARDS.ENGAGEMENT'),
      value: engagement.total ?? 0,
      delta: pctDelta(engagement.total || 0, prevEngagement.total || 0),
      icon: 'i-lucide-heart',
      color: 'text-rose-600',
    },
    {
      label: t('SOCIAL_MONITORING.CARDS.TREND'),
      value: `${trend > 0 ? '+' : ''}${trend}%`,
      delta: null,
      icon: trend >= 0 ? 'i-lucide-trending-up' : 'i-lucide-trending-down',
      color: trend >= 0 ? 'text-emerald-600' : 'text-rose-600',
    },
  ];
});

const deltaClass = delta => {
  if (delta === null || delta === undefined) return 'text-n-slate-11';
  if (delta > 0) return 'text-emerald-600';
  if (delta < 0) return 'text-rose-600';
  return 'text-n-slate-11';
};

const deltaIcon = delta => {
  if (delta === null || delta === undefined) return '';
  if (delta > 0) return 'i-lucide-arrow-up-right';
  if (delta < 0) return 'i-lucide-arrow-down-right';
  return 'i-lucide-minus';
};
</script>

<template>
  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <div
      v-for="card in cards"
      :key="card.label"
      class="rounded-xl border border-n-weak bg-n-background p-4"
    >
      <div class="flex items-start justify-between">
        <div>
          <p
            class="text-xs font-medium uppercase tracking-wide text-n-slate-11"
          >
            {{ card.label }}
          </p>
          <p class="mt-2 text-2xl font-bold text-n-slate-12">
            {{ card.value }}
          </p>
          <p
            v-if="card.delta !== null && card.delta !== undefined"
            class="mt-1 flex items-center gap-1 text-[11px] font-medium"
            :class="deltaClass(card.delta)"
          >
            <span :class="deltaIcon(card.delta)" class="size-3" />
            <span>
              {{ card.delta > 0 ? '+' : '' }}{{ card.delta }}{{ card.deltaSuffix || '%' }}
            </span>
            <span class="text-n-slate-11">vs anterior</span>
          </p>
        </div>
        <span :class="[card.icon, card.color]" class="size-6" />
      </div>
    </div>
  </div>
</template>
